// src/pages/IncompleteSignup.jsx
//
// Shown when someone is authenticated (a real Supabase session exists) but
// has NO firm anywhere — no `firms` row, no `firm_users` row. This is the
// orphaned-auth-user shape: register_firm creates the Supabase Auth user
// BEFORE inserting the firms row, so a failure in between used to leave a
// real, logged-in-able account with nothing behind it and no way to fix it
// (see kailux-fixes-log.md and kailux-master-plan.md item 16 — a real
// production account, mamuhohi5862@gmail.com, was found in exactly this
// state 5 Sept 2026 and had to be hand-deleted via direct Supabase SQL
// before it could be re-signed-up). register_firm itself is now fixed to
// roll back cleanly instead of leaving this behind — this page is the
// self-service safety net for anyone who got stuck here before that fix
// existed, or from any other future code path that manages to produce the
// same shape again.
//
// ProtectedRoute routes here whenever `user` is set (real session) but
// `firm` is null once loading has finished — see ProtectedRoute.jsx.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { resetIncompleteSignup } from '../api/kailux'
import { AlertTriangle, Mail } from 'lucide-react'


import { T, alpha } from '../theme/theme'
export default function IncompleteSignup() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [resetting, setResetting] = useState(false)
  const [error,     setError]     = useState('')

  const handleReset = async () => {
    setResetting(true); setError('')
    try {
      await resetIncompleteSignup()
      await logout()
      navigate('/signup')
    } catch (err) {
      setError(err.response?.data?.detail
        || 'Could not reset your account automatically. Please email support@kailux.co.za.')
      setResetting(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.tx,
      fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center',
      justifyContent:'center', padding:'24px' }}>
      <div style={{ width:'100%', maxWidth:'520px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'32px' }}>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
          <AlertTriangle size={22} color={T.yl} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
            Your signup didn't finish
          </div>
        </div>

        <p style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'20px' }}>
          We can see you're signed in{user?.email ? <> as <strong style={{ color:T.tx }}>{user.email}</strong></> : ''},
          but your firm account was never fully created — something interrupted your
          original signup partway through. Nothing was lost on your end; this just
          needs a quick reset so you can sign up again with the same email address.
        </p>

        {error && (
          <div style={{ background:alpha(T.rd, 0.08), border:`1px solid ${alpha(T.rd, 0.3)}`,
            borderRadius:'10px', padding:'14px', marginBottom:'20px', fontSize:'13px', color:T.rd }}>
            {error}
          </div>
        )}

        <button onClick={handleReset} disabled={resetting}
          style={{ width:'100%', padding:'12px', borderRadius:'8px', border:'none',
            background: resetting ? T.mt : T.prg,
            color:T.onpri, fontSize:'14px', fontWeight:'700',
            cursor: resetting ? 'not-allowed' : 'pointer' }}>
          {resetting ? 'Resetting…' : 'Reset & sign up again'}
        </button>

        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'20px',
          paddingTop:'18px', borderTop:`1px solid ${T.bd}` }}>
          <Mail size={13} color={T.mt} />
          <span style={{ fontSize:'12px', color:T.mt }}>
            Prefer we sort it out directly? Email{' '}
            <a href="mailto:support@kailux.co.za" style={{ color:T.or }}>support@kailux.co.za</a>
          </span>
        </div>
      </div>
    </div>
  )
}
